import type { UserPermissions } from '../permissions'
import {
  buildAppRolePermissions,
  buildCreditRolePermissions,
  buildCrtRolePermissions,
} from '../role-builders'
import _BUSINESS_ADMIN from './list/business-admin'
import _COMPLIANCE from './list/compliance'
import _CREDIT_ADMIN from './credit/credit-admin'
import _CRT_ADMIN from './crt/crt-admin'
import _CRT_EXECUTIVE from './crt/crt-executive'
import _CRT_FINANCIAL_ANALYST from './crt/crt-financial-analyst'
import _CRT_USER from './crt/crt-user'
import _CUSTOMER_SERVICE from './list/customer-service'
import _IDT_COMMERCIAL_MARKETING from './list/idt-commercial-marketing'
import _IDT_SERVICES from './list/idt-services'
import _INSIDE_SALES from './list/inside-sales'
import _IT_ADMIN from './list/it-admin'
import _KEY_ACCOUNT from './list/key-account'
import _MARKETING_B2B from './list/marketing-b2b'
import _MARKETING_SYSTEM from './list/marketing-systems'
import _OTC from './list/otc'
import _PRICING from './list/pricing'
import _PRODUCT_PORTFOLIO from './list/product-portfolio'
import _SUPPORT from './list/support'
import _TMAR from './list/tmar'

export const ROLE_PERMISSIONS = {
  BUSINESS_ADMIN: buildAppRolePermissions(_BUSINESS_ADMIN),
  COMPLIANCE: buildAppRolePermissions(_COMPLIANCE),
  CREDIT_ADMIN: buildCreditRolePermissions(_CREDIT_ADMIN),
  CRT_ADMIN: buildCrtRolePermissions(_CRT_ADMIN),
  CRT_EXECUTIVE: buildCrtRolePermissions(_CRT_EXECUTIVE),
  CRT_USER: buildCrtRolePermissions(_CRT_USER),
  CUSTOMER_SERVICE: buildAppRolePermissions(_CUSTOMER_SERVICE),
  CRT_FINANCIAL_ANALYST: buildCrtRolePermissions(_CRT_FINANCIAL_ANALYST),
  IDT_COMMERCIAL_MARKETING: buildAppRolePermissions(_IDT_COMMERCIAL_MARKETING),
  IDT_SERVICES: buildAppRolePermissions(_IDT_SERVICES),
  INSIDE_SALES: buildAppRolePermissions(_INSIDE_SALES),
  IT_ADMIN: buildAppRolePermissions(_IT_ADMIN),
  KEY_ACCOUNT: buildAppRolePermissions(_KEY_ACCOUNT),
  MARKETING_B2B: buildAppRolePermissions(_MARKETING_B2B),
  MARKETING_SYSTEM: buildAppRolePermissions(_MARKETING_SYSTEM),
  OTC: buildAppRolePermissions(_OTC),
  PRICING: buildAppRolePermissions(_PRICING),
  PRODUCT_PORTFOLIO: buildAppRolePermissions(_PRODUCT_PORTFOLIO),
  SUPPORT: buildAppRolePermissions(_SUPPORT),
  TMAR: buildAppRolePermissions(_TMAR),
} as const satisfies Record<string, UserPermissions>

export type RoleName = keyof typeof ROLE_PERMISSIONS

export const ROLES = Object.freeze(
  Object.fromEntries(Object.keys(ROLE_PERMISSIONS).map((key) => [key, key])) as Record<
    RoleName,
    RoleName
  >,
)

export const ROLES_LIST = Object.keys(ROLE_PERMISSIONS) as RoleName[]

export const ROLES_LABELS = {
  BUSINESS_ADMIN: 'Business Admin',
  COMPLIANCE: 'Compliance',
  CREDIT_ADMIN: 'Credit Admin',
  CRT_ADMIN: 'CRT Admin',
  CRT_EXECUTIVE: 'CRT Executive',
  CRT_USER: 'CRT User',
  CUSTOMER_SERVICE: 'Customer Service',
  CRT_FINANCIAL_ANALYST: 'Financial Analyst',
  IDT_COMMERCIAL_MARKETING: 'IDT Commercial Marketing',
  IDT_SERVICES: 'IDT Services',
  INSIDE_SALES: 'Inside Sales',
  IT_ADMIN: 'IT Admin',
  KEY_ACCOUNT: 'Key Account',
  MARKETING_B2B: 'Marketing B2B',
  MARKETING_SYSTEM: 'Marketing System',
  OTC: 'OTC',
  PRICING: 'Pricing',
  PRODUCT_PORTFOLIO: 'Product Portfolio',
  SUPPORT: 'Support',
  TMAR: 'TMAR',
} as const satisfies Record<RoleName, string>

export type RoleLabel = (typeof ROLES_LABELS)[keyof typeof ROLES_LABELS]

/**
 * Roles that only mean anything on a Nova environment, the same way `NOVA_ONLY_PERMISSIONS` works
 * for permissions. The role definition exists everywhere so it type-checks on every build, but a
 * mainline deployment neither seeds it, lists it for assignment, nor reports on it, so the SOX
 * users and role-permissions reports never describe a role no mainline user can hold.
 */
export const NOVA_ONLY_ROLES = ['OTC'] as const satisfies readonly RoleName[]

export type NovaOnlyRole = (typeof NOVA_ONLY_ROLES)[number]

const NOVA_ONLY_ROLE_SET: ReadonlySet<string> = new Set(NOVA_ONLY_ROLES)

export function isNovaOnlyRole(role: string): role is NovaOnlyRole {
  return NOVA_ONLY_ROLE_SET.has(role)
}

/** The given role names, minus the Nova-only ones when this is not a Nova environment. */
export function withoutNovaOnlyRoles<T extends string>(
  roleNames: readonly T[],
  isNova: boolean,
): T[] {
  return isNova ? [...roleNames] : roleNames.filter((r) => !isNovaOnlyRole(r))
}
